import React from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import type { ReviewCellRendererProps } from '../helpers';
import { Icon, Tooltip, TooltipPosition } from 'venn-ui-kit';

export const StatusCellRenderer = ({
  additionalMetadata,
  data,
  errors,
}: ICellRendererParams & ReviewCellRendererProps) => {
  let statusIcon;

  const { ownershipContextIds, seriesId } = data;
  const { isDeleted, isNew } = additionalMetadata.perSeries[seriesId]!;
  const hasUpdates = data.newDataCount > 0;

  // new
  if (isNew) {
    statusIcon = ownershipContextIds ? (
      // new, ownership set
      <Icon type="circle-check" className="text-venn-green" />
    ) : (
      // new, no ownership
      <Tooltip
        usePortal
        position={TooltipPosition.Left}
        content="Assign ownership(s) to this investment in order to upload."
      >
        <Icon type="diamond-exclamation" className="text-venn-orange" />
      </Tooltip>
    );
  }

  // existing, with updates
  if (!isNew && hasUpdates) statusIcon = <Icon type="circle-check" className="text-venn-green" />;

  // existing, no updates
  if (!isNew && !hasUpdates) {
    statusIcon = (
      <Tooltip
        usePortal
        position={TooltipPosition.Left}
        content="No new observations were found. This data will not be uploaded unless you select ‘overwrite’, or rename the investment to mark as new."
      >
        <Icon type="circle-dashed" className="text-venn-grey-60" />
      </Tooltip>
    );
  }

  // errored
  if (errors.length && errors.find((error) => error.seriesId === seriesId)) {
    statusIcon = (
      <Tooltip
        usePortal
        position={TooltipPosition.Left}
        content="This data contains errors such as gaps between date periods and will not be uploaded."
      >
        <Icon type="triangle-exclamation" className="text-venn-error" />
      </Tooltip>
    );
  }

  // removed
  if (isDeleted) {
    statusIcon = (
      <Tooltip usePortal position={TooltipPosition.Left} content="Restore investment.">
        <Icon type="arrow-rotate-left" className="text-venn-dark-blue" />
      </Tooltip>
    );
  }

  return <div className="py-[8px] text-[14px] leading-[16px]">{statusIcon}</div>;
};
